import type {
  GetChannelsMessagesByCidItem,
  GetUsersByUserIdResponse,
} from "@birdwatch/client";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { useMemo } from "react";
import {
  AudioRecordPlayer,
  AudioRecordPlayerDuration,
  AudioRecordPlayerOption,
  AudioRecordPlayerTogglePlay,
  AudioRecordPlayerWave,
} from "@/features/shared/audio-recorder/_components/audio-record-player";
import { convertAudioUrlToPlayable } from "@/features/shared/audio-recorder/_libs/convert-audio-url-to-playable";
import type { ExtractItem } from "@/types/openapi";
import { AttachmentAnalysisListener } from "./attachment-analysis-listener";
import { AudioRecordTranscriptPopover } from "./audio-record-transcipt-popover";

/**
 * A voice note is 496px wide wherever it appears, rather than shrinking to whatever
 * caption happens to sit beside it: the waveform fills its container and contributes no
 * width of its own, so without this the same recording drew a different player in every
 * thread. 496px is the bubble's inner width at `max-w-lg` less its `p-2`.
 *
 * `w-full` below `sm` fills a phone; `max-w-full` keeps it inside a narrow column on a
 * wide viewport (the order aside, the pinned strip).
 */
const AUDIO_ATTACHMENT_WIDTH = "w-full max-w-full sm:w-124";

export const AudioRecordAttachment = ({
  attachment,
  userCreated,
  className,
}: {
  attachment: ExtractItem<GetChannelsMessagesByCidItem["attachments"]>;
  userCreated: GetUsersByUserIdResponse;
  className?: string;
}) => {
  const playableUrl = useMemo(
    () => convertAudioUrlToPlayable(attachment?.contentUri),
    [attachment?.contentUri],
  );

  return (
    <div
      className={cn(
        /* sorted */
        AUDIO_ATTACHMENT_WIDTH,
        "flex",
        "flex-col gap-2",
        "mt-1 pb-1",
        className,
      )}
    >
      {attachment?.id && isEmpty(attachment?.attachmentExtra?.summary) && (
        <AttachmentAnalysisListener
          attachmentId={attachment.id}
          createdAt={attachment.createdAt}
        />
      )}
      <AudioRecordPlayer recordedUrl={playableUrl} className="bg-bg-light-blur">
        <AudioRecordPlayerTogglePlay />
        <AudioRecordPlayerWave />
        <AudioRecordPlayerDuration />
        <AudioRecordPlayerOption />
      </AudioRecordPlayer>
      <div className="border-l-2 border-border-dark-min pl-2 ">
        <p className="text-xs text-text-dark-high">
          {attachment?.attachmentExtra?.summary
            ?.split(" ")
            .slice(0, 12)
            .join(" ")}
          <span>
            {(attachment?.attachmentExtra?.summary?.split(" ").length ?? 0) > 12
              ? "... "
              : " "}
          </span>
          <AudioRecordTranscriptPopover
            attachment={attachment}
            userCreated={userCreated}
          />
        </p>
      </div>
    </div>
  );
};
